import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { Common } from "lib/types"
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormResetField,
  UseFormSetValue
} from "react-hook-form"
import SingleSelect from "components/Ui/Form/SingleSelect"
import { FrontendOutcome } from "lib/types/discount"
import {
  DiscountValue,
  InputDiscountValueType,
  InputOutcome
} from "lib/types/generated/graphql-types"
import Input from "components/Ui/Form/Input"
import FixedDiscounts from "./FixedDiscounts"
import { Percentage as PercentageHelper } from "helpers/Percentage"
import { WithAuth } from "../Auth"
import { PermissionGroups } from "../../routes/constants"
import FallbackInput from "../Ui/Form/FallbackInput"

const SubOutcomeWrapper = styled.div`
  background: ${(p) => p.theme.colors.white};
  padding: 1rem;
  margin-bottom: 2rem;
  margin-top: 1rem;
  border: 0.1rem solid ${(p) => p.theme.colors.greyLight};
  border-radius: 0.6rem;
`

const Percentage = styled.div`
  width: 100%;
  flex: auto !important;
  margin-top: 2rem;
  gap: 1rem;
  position: relative;
`

type Props = {
  index: number
  outcome?: FrontendOutcome | undefined
  currencyOptions: Common.Option[]
  control: Control<InputOutcome>
  setValue: UseFormSetValue<InputOutcome>
  errors: FieldErrors<InputOutcome>
  register: UseFormRegister<InputOutcome>
  resetField: UseFormResetField<InputOutcome>
  hideDiscountQuantity?: boolean
}

export const SubOutcome = ({
  index,
  outcome,
  currencyOptions,
  control,
  setValue,
  errors,
  register,
  resetField,
  hideDiscountQuantity
}: Props) => {
  const [valueType, setValueType] = useState<string>(outcome?.data?.discountValue?.type ?? "")
  const [fixedDiscounts, setFixedDiscounts] = useState<Common.Money[]>(() => {
    return outcome?.data?.subOutcomes?.[index]?.discountValue?.fixedDiscounts ?? []
  })

  const [relativeDiscount, setRelativeDiscount] = useState<Common.RelativeDiscount>({
    percentage: outcome?.data?.subOutcomes?.[index]?.discountValue?.percentage ?? undefined,
    percentageDecimals: outcome?.data?.subOutcomes?.[index]?.discountValue?.percentageDecimals ?? 0
  })

  const updateValueType = (valueType: Common.Option) => {
    setValue(
      `subOutcomes.${index}.discountValue.type` as "discountValue.type",
      valueType.value as InputDiscountValueType
    )
    if (valueType.value === InputDiscountValueType.Relative) {
      setValue(
        `subOutcomes.${index}.discountValue.fixedDiscounts` as "discountValue.fixedDiscounts",
        []
      )
    } else if (valueType.value === InputDiscountValueType.Fixed) {
      setValue(`subOutcomes.${index}.discountValue.percentage` as "discountValue.percentage", null)
    }
    setValueType(valueType.value)
  }

  const getOption = (type: string) => {
    switch (type) {
      case "RELATIVE":
        return {
          value: InputDiscountValueType.Relative,
          label: "Percentage %"
        }
      case "FIXED":
        return {
          value: InputDiscountValueType.Fixed,
          label: "Fixed amount"
        }
      default:
        return {
          label: "Select discount value type",
          value: ""
        }
    }
  }

  useEffect(() => {
    resetField(`subOutcomes.${index}.discountValue.percentage`, {
      defaultValue: relativeDiscount.percentage
    })
    resetField(`subOutcomes.${index}.discountValue.percentageDecimals`, {
      defaultValue: relativeDiscount.percentageDecimals
    })
  }, [relativeDiscount])

  useEffect(() => {
    setValue(`subOutcomes.${index}.discountValue.fixedDiscounts`, fixedDiscounts)
  }, [fixedDiscounts])

  return (
    <SubOutcomeWrapper>
      <WithAuth
        requiredGroups={PermissionGroups.discounts.write}
        fallback={
          <FallbackInput
            label={"Trigger quantity"}
            value={outcome?.data?.subOutcomes?.[index]?.triggerQuantity?.toString() ?? undefined}
          />
        }
      >
        <Controller
          name={`subOutcomes.${index}.triggerQuantity`}
          render={({ field }) => (
            <Input
              {...field}
              type="number"
              label="Trigger quantity *"
              description="Quantity that is used as a trigger"
              errors={errors}
              hooksErrorMessage={true}
            />
          )}
          defaultValue={outcome?.data?.subOutcomes?.[index]?.triggerQuantity ?? undefined}
          control={control}
          rules={{
            required: "This is a required field",
            pattern: {
              value: /^\d+$/,
              message: "Only integers 0 - 9 is allowed"
            }
          }}
        />
      </WithAuth>
      {!hideDiscountQuantity && (
        <WithAuth
          requiredGroups={PermissionGroups.discounts.write}
          fallback={
            <FallbackInput
              label={"Discount quantity"}
              value={outcome?.data?.subOutcomes?.[index]?.discountQuantity?.toString() ?? undefined}
            />
          }
        >
          <Controller
            name={`subOutcomes.${index}.discountQuantity`}
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  name={`subOutcomes[${index}].discountQuantity`}
                  type="number"
                  label="Discount quantity *"
                  description={"Quantity that will be discounted."}
                  errors={errors}
                  hooksErrorMessage={true}
                />
              </>
            )}
            defaultValue={outcome?.data?.subOutcomes?.[index]?.discountQuantity ?? undefined}
            control={control}
            rules={{
              required: "This is a required field",
              pattern: {
                value: /^\d+$/,
                message: "Only integers 0 - 9 is allowed"
              },
              validate: (value, formValues) =>
                Number(formValues?.subOutcomes?.[index]?.triggerQuantity ?? 0) >=
                  Number(value ?? 0) || "Discount quantity cannot be bigger than trigger quantity."
            }}
          />
        </WithAuth>
      )}
<WithAuth requiredGroups={PermissionGroups.discounts.write} mode={"disable"}>
  <Controller
    name={`subOutcomes.${index}.discountValue.type`}
    render={({ field }) => (
      <SingleSelect
        {...field}
        options={[getOption("FIXED"), getOption("RELATIVE")]}
        label="Discount output"
        setValue={updateValueType}
        defaultValue={
          (outcome?.data?.subOutcomes?.[index]?.discountValue?.type as string) !== undefined
            ? getOption(
              (outcome?.data?.subOutcomes?.[index]?.discountValue as DiscountValue).type
            )
            : getOption("")
        }
      />
    )}
    control={control}
  />
</WithAuth>
      <>
        {valueType === InputDiscountValueType.Relative && (
          <Percentage>
            <WithAuth requiredGroups={PermissionGroups.discounts.write} mode={"disable"} >
              <Controller
                name={`subOutcomes.${index}.discountValue.percentage`}
                defaultValue={relativeDiscount.percentage}
                render={({ field }) => (
                  <Input
                    {...field}
                    name={`subOutcomes.${index}.discountValue.percentage`}
                    type="number"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setRelativeDiscount({
                        ...relativeDiscount,
                        percentage: parseInt(event.target.value)
                      })
                    }
                    placeholder="Percentage, 1 - 100"
                    label="Percentage value *"
                    errors={errors}
                  />
                )}
                control={control}
                rules={{
                  required: "This is a required field",
                  pattern: {
                    value: /^\d+$/,
                    message: "Only integers 0 - 9 is allowed"
                  },
                  min: {
                    value: 1,
                    message: "Min value is 1"
                  },
                  max: { value: 100000, message: "Max value is 100" }
                }}
              />
            </WithAuth>
            <WithAuth requiredGroups={PermissionGroups.discounts.write} mode={"disable"}>
              <Controller
                name={`subOutcomes.${index}.discountValue.percentageDecimals`}
                defaultValue={relativeDiscount.percentageDecimals}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Percentage decimals *"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setRelativeDiscount({
                        ...relativeDiscount,
                        percentageDecimals: parseInt(event.target.value)
                      })
                    }
                    errors={errors}
                  />
                )}
                control={control}
                rules={{
                  pattern: {
                    value: /^\d+$/,
                    message: "Only integers 0 - 9 is allowed"
                  },
                  min: {
                    value: 0,
                    message: "Min value is 0"
                  },
                  max: { value: 3, message: "Max value is 3" }
                }}
              />
            </WithAuth>
            <div>
              <strong>Formatted: </strong>
              {new PercentageHelper(
                relativeDiscount.percentage,
                relativeDiscount.percentageDecimals
              ).formatPercentage()}
            </div>
          </Percentage>
        )}

        {valueType === InputDiscountValueType.Fixed && (
          <FixedDiscounts
            {...register(`subOutcomes.${index}.discountValue.fixedDiscounts`)}
            fixedDiscounts={fixedDiscounts}
            setFixedDiscounts={setFixedDiscounts}
            currencyOptions={currencyOptions}
          />
        )}
      </>
    </SubOutcomeWrapper>
  )
}
